import React from 'react';
import { Card, CardContent, Typography, Chip, Box, Divider, useTheme } from '@mui/material';
import { PlasticRoute } from '../../types';

interface PlasticRouteCardProps {
  route: PlasticRoute;
  onClick: (route: PlasticRoute) => void;
  isSelected: boolean;
}

const PlasticRouteCard: React.FC<PlasticRouteCardProps> = ({ route, onClick, isSelected }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  return (
    <Card
      onClick={() => onClick(route)}
      sx={{
        cursor: 'pointer',
        height: '100%',
        border: isSelected
          ? '3px solid'
          : '1px solid',
        borderColor: isSelected
          ? (isDarkMode ? '#66bb6a' : '#2e7d32')
          : (isDarkMode ? '#444444' : '#e0e0e0'),
        backgroundColor: isSelected
          ? (isDarkMode ? '#1b5e20' : '#f1f8e9')
          : (isDarkMode ? '#1e1e1e' : '#ffffff'),
        transition: 'all 0.3s ease',
        '&:hover': {
          borderColor: isDarkMode ? '#81c784' : '#4caf50',
          backgroundColor: isDarkMode ? '#2e7d32' : '#f9fbe7',
        },
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography
            variant="h6"
            component="div"
            sx={{
              color: isDarkMode ? '#66bb6a' : '#2e7d32',
              fontWeight: 600
            }}
          >
            {route.name}
          </Typography>
          <Chip label={route.processType} color="default" size="small" />
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
          {route.description}
        </Typography>

        <Divider sx={{ my: 1.5, borderColor: isDarkMode ? '#444444' : '#e0e0e0' }} />

        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
            原料:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {route.feedstock.map((item) => (
              <Chip key={item} label={item} size="small" color="primary" variant="outlined" />
            ))}
          </Box>
        </Box>

        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
            产品:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {route.products.map((item) => (
              <Chip key={item} label={item} size="small" color="success" variant="outlined" />
            ))}
          </Box>
        </Box>

        {route.byproducts && route.byproducts.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
              副产品:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {route.byproducts.map((item) => (
                <Chip key={item} label={item} size="small" color="default" variant="outlined" />
              ))}
            </Box>
          </Box>
        )}

        {route.catalyst && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
              催化剂:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {route.catalyst.split(/[、,，]/).filter(Boolean).map((item) => (
                <Chip key={item} label={item.trim()} size="small" color="secondary" variant="outlined" />
              ))}
            </Box>
          </Box>
        )}

        {(route.temperature || route.pressure || (route.keyParameters && route.keyParameters.length > 0)) && (
          <>
            <Divider sx={{ my: 1.5, borderColor: isDarkMode ? '#444444' : '#e0e0e0' }} />

            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                工艺参数:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {route.temperature && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: isDarkMode ? '#ffb74d' : '#ff9800',
                      fontSize: '0.85rem',
                    }}
                  >
                    温度: {route.temperature}
                  </Typography>
                )}
                {route.pressure && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: isDarkMode ? '#81d4fa' : '#0288d1',
                      fontSize: '0.85rem',
                    }}
                  >
                    压力: {route.pressure}
                  </Typography>
                )}
              </Box>
            </Box>

            {route.keyParameters && route.keyParameters.length > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                  主反应化学方程:
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {route.keyParameters.map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
                      <Typography
                        sx={{
                          fontSize: '0.85rem',
                          fontWeight: 700,
                          color: isDarkMode ? '#ce93d8' : '#9c27b0',
                          minWidth: 16,
                        }}
                      >
                        {index + 1}.
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: isDarkMode ? '#ce93d8' : '#9c27b0',
                          fontSize: '0.85rem',
                          lineHeight: 1.5,
                        }}
                      >
                        {item}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </>
        )}

        {route.applications && route.applications.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
              典型应用:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {route.applications.slice(0, 3).map((item) => (
                <Chip key={item} label={item} size="small" color="secondary" variant="outlined" />
              ))}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default PlasticRouteCard;
